import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';
import 'rxjs/add/operator/take';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/distinctUntilChanged';

import { LearningFacadeService } from '../../../services/learning-facade.service';
import {
  SkillSpecializationResponse,
  SkillProgressResponse,
  SkillTestResultResponse,
  SkillLearningGeneralSettingsResponse,
  PracticeLevelResponse,
  TheoryLevelResponse,
  SkillLearningErrorResponse,
  SkillLevelType,
  LearningHelpRow,
  mergeSkillLearningGeneralSettings,
} from '../../../models/skill-learning.model';
import { UiLocaleService } from '../../../../../../services/ui-locale.service';
import { TranslationBundle } from '../../../models/translation-bundle.model';
import {
  SKILL_LEARNING_LEVEL_TAB,
  SKILL_LEARNING_LEVEL_COUNT,
  SKILL_LEARNING_FIRST_INDEX,
  SKILL_LEARNING_SESSION_MESSAGE,
  SKILL_LEARNING_EMPTY_TEXT,
  SKILL_LEARNING_ROUTE,
  SKILL_SPECIALIZATION_ROUTE_PARAM,
  SKILL_CONFIGURATION_ERROR,
  SKILL_LEARNER_ACTION_MESSAGE,
  SKILL_LEVEL_TYPE,
  SKILL_PRACTICE_TASK_TYPE,
  SKILL_PRACTICE_TASK_STATUS,
  SkillLearningLevelTab,
  normalizePublishedSkillLevelCount,
  resolveSkillLevelType,
} from '../../../options/skill-learning.option';

const ROUTING_NOT_CONFIGURED = 'CROSS_DEPARTMENT_ROUTING_NOT_CONFIGURED';
const ROUTING_REQUIRED_KEY = 'SKILL_LEARNING_ROUTING_REQUIRED';
const ROUTE_SEPARATOR = '/';
const EDIT_ROUTE = '/edit/general';
const LEVEL_LOCKED_HINT_KEY = 'SKILL_LEARNING_SESSION_LEVEL_LOCKED_HINT';
const PROGRESS_LOAD_ERROR_KEY = 'SKILL_LEARNING_SESSION_PROGRESS_LOAD_ERROR';
const PROGRESS_LOADING_KEY = 'SKILL_LEARNING_COMMON_LOADING';
const INITIAL_REFRESH_ATTEMPTS = 0;

@Component({
  selector: 'app-learning-session-container',
  templateUrl: './learning-session-container.component.html',
  styleUrls: ['./learning-session-container.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LearningSessionContainerComponent implements OnInit, OnDestroy {
  readonly levelTabs = SKILL_LEARNING_LEVEL_TAB;
  readonly levelCounts = SKILL_LEARNING_LEVEL_COUNT;
  readonly levelTypes = SKILL_LEVEL_TYPE;
  readonly practiceTaskTypes = SKILL_PRACTICE_TASK_TYPE;

  specialization: SkillSpecializationResponse;
  progress: SkillProgressResponse;
  generalSettings: SkillLearningGeneralSettingsResponse = mergeSkillLearningGeneralSettings(null);
  isLoading = true;
  hasError = false;
  isProgressLoading = false;
  hasProgressError = false;
  levelActionErrorKey = SKILL_LEARNING_EMPTY_TEXT;
  routingNotConfigured = false;
  viewMode: SkillLearningLevelTab = SKILL_LEARNING_LEVEL_TAB.LEVEL_1;
  level2ReviewDescriptionTitleKey = 'SKILL_LEARNING_SESSION_LEVEL2_TEST_REVIEW_DIRECTIONS_TITLE';
  level2StartButtonKey = 'SKILL_LEARNING_PRACTICE_START';
  learningHelpRows: LearningHelpRow[] = [
    { titleKey: 'SKILL_LEARNING_TREE_HELP_LEVEL1_TITLE', textKey: 'SKILL_LEARNING_TREE_HELP_LEVEL1_TEXT' },
    { titleKey: 'SKILL_LEARNING_TREE_HELP_LEVEL2_REVIEW_TITLE', textKey: 'SKILL_LEARNING_TREE_HELP_LEVEL2_REVIEW_TEXT' },
    { titleKey: 'SKILL_LEARNING_TREE_HELP_LEVEL2_PRACTICE_TITLE', textKey: 'SKILL_LEARNING_TREE_HELP_LEVEL2_PRACTICE_TEXT' },
    { titleKey: 'SKILL_LEARNING_TREE_HELP_LEVEL3_TITLE', textKey: 'SKILL_LEARNING_TREE_HELP_LEVEL3_TEXT' },
  ];

  private readonly destroy$ = new Subject<void>();
  private readonly contextChanged$ = new Subject<void>();
  specializationId: string;
  private keepTestResultAfterProgressReload = false;
  private completionRefreshQueued = false;
  private destroyed = false;
  private translationRefreshTimer: NodeJS.Timer = null;
  private translationRefreshAttempts = INITIAL_REFRESH_ATTEMPTS;
  private readonly translationRefreshDelayMs = 5000;
  private readonly translationRefreshMaxAttempts = 12;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private facade: LearningFacadeService,
    private cdr: ChangeDetectorRef,
    private uiLocaleService: UiLocaleService,
  ) {}

  ngOnInit(): void {
    this.subscribeToLevelActionErrors();
    this.route.params
      .map(params => params[SKILL_SPECIALIZATION_ROUTE_PARAM])
      .distinctUntilChanged()
      .takeUntil(this.destroy$)
      .subscribe(id => this.openSpecialization(id));
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.clearTranslationRefreshTimer();
    this.destroy$.next();
    this.destroy$.complete();
    this.contextChanged$.complete();
  }

  private openSpecialization(id: string): void {
    this.contextChanged$.next();
    this.clearTranslationRefreshTimer();
    this.resetCourseState();
    this.specializationId = id;
    if (!id) {
      this.router.navigate([SKILL_LEARNING_ROUTE.LEARNING]);
      return;
    }
    this.loadData();
  }

  private resetCourseState(): void {
    this.specialization = null;
    this.progress = null;
    this.viewMode = SKILL_LEARNING_LEVEL_TAB.LEVEL_1;
    this.keepTestResultAfterProgressReload = false;
    this.completionRefreshQueued = false;
    this.isProgressLoading = false;
    this.hasProgressError = false;
    this.levelActionErrorKey = SKILL_LEARNING_EMPTY_TEXT;
    this.routingNotConfigured = false;
  }

  get currentLevel(): number {
    return this.facade.resolveCurrentLevel(this.progress);
  }

  get activeLevelNumber(): number {
    return this.viewMode + SKILL_LEARNING_LEVEL_COUNT.ONE;
  }

  get activeLevelType(): SkillLevelType {
    return resolveSkillLevelType(
      this.activeLevelNumber,
      this.specialization ? this.specialization.publishedLevel2Type : null,
      this.specialization ? this.specialization.publishedLevel3Type : null,
    );
  }

  get isActiveTestLevel(): boolean {
    return this.activeLevelType === SKILL_LEVEL_TYPE.TEST;
  }

  get activeTheoryLevel(): TheoryLevelResponse | null {
    if (!this.specialization) {
      return null;
    }
    if (this.activeLevelNumber === SKILL_LEARNING_LEVEL_COUNT.ONE) {
      return this.specialization.theoryLevel;
    }
    return this.activeLevelNumber === SKILL_LEARNING_LEVEL_COUNT.TWO
      ? this.specialization.theoryLevel2 || null
      : this.specialization.theoryLevel3 || null;
  }

  get activeTheoryDescription(): string {
    const theory = this.activeTheoryLevel;
    return theory
      ? this.localizedText(theory.description, theory.descriptionTranslation)
      : SKILL_LEARNING_EMPTY_TEXT;
  }

  get activeTheoryTopics(): string[] {
    const theory = this.activeTheoryLevel;
    if (!theory) {
      return [];
    }
    const translations = theory.topicTranslations || [];
    return (theory.topics || []).map((topic, index) =>
      this.localizedText(topic, translations[index])).filter(topic => !!topic);
  }

  get activePracticeLevel(): PracticeLevelResponse | null {
    if (!this.specialization || this.activeLevelNumber === SKILL_LEARNING_LEVEL_COUNT.ONE) {
      return null;
    }
    return this.activeLevelNumber === SKILL_LEARNING_LEVEL_COUNT.TWO
      ? this.specialization.practiceLevel2 : this.specialization.practiceLevel3;
  }

  get activePracticeDescription(): string {
    const practice = this.activePracticeLevel;
    return practice
      ? this.localizedText(practice.description, practice.descriptionTranslation)
      : SKILL_LEARNING_EMPTY_TEXT;
  }

  get isActiveLevelLocked(): boolean {
    return !this.isLevelAvailable(this.viewMode);
  }

  get levelLockedHintKey(): string {
    if (this.isProgressLoading) {
      return PROGRESS_LOADING_KEY;
    }
    return this.hasProgressError ? PROGRESS_LOAD_ERROR_KEY : LEVEL_LOCKED_HINT_KEY;
  }

  isLevelAvailable(viewMode: SkillLearningLevelTab): boolean {
    const level = viewMode + SKILL_LEARNING_LEVEL_COUNT.ONE;
    if (this.destroyed || !this.specialization || !this.progress || this.hasError
      || viewMode < SKILL_LEARNING_LEVEL_TAB.LEVEL_1 || level > this.publishedLevelCount) {
      return false;
    }
    return this.currentLevel >= level
      || (!this.isProgressLoading && !this.hasProgressError && this.currentLevel >= viewMode);
  }

  get isActiveLevelPassed(): boolean {
    return this.currentLevel >= this.activeLevelNumber;
  }

  get shouldShowLevel2Review(): boolean {
    return this.activeLevelNumber === SKILL_LEARNING_LEVEL_COUNT.TWO
      && this.activeLevelType === SKILL_LEVEL_TYPE.TASK;
  }

  get isActivePracticeDisabled(): boolean {
    if (this.isActiveLevelLocked) {
      return true;
    }
    if (this.activeLevelNumber === SKILL_LEARNING_LEVEL_COUNT.TWO) {
      return !this.progress || !this.progress.canCreateLevel2PracticeTask;
    }
    return !this.hasPracticeLevelData(this.activePracticeLevel);
  }

  get isActiveTestBlockedByReview(): boolean {
    return this.activeLevelNumber === SKILL_LEARNING_LEVEL_COUNT.ONE
      && this.isLevel2TestReviewPassed;
  }

  get publishedLevelCount(): number {
    return normalizePublishedSkillLevelCount(
      this.specialization && this.specialization.publishedLevelCount,
    );
  }

  get hasLevel2(): boolean {
    return this.publishedLevelCount >= SKILL_LEARNING_LEVEL_COUNT.TWO;
  }

  get hasPublishedLevels(): boolean {
    return this.publishedLevelCount >= SKILL_LEARNING_LEVEL_COUNT.ONE;
  }

  get canRequestNextLevel(): boolean {
    return !this.isProgressLoading && !this.hasProgressError
      && this.isLevelAvailable(<SkillLearningLevelTab>(this.viewMode + SKILL_LEARNING_LEVEL_COUNT.ONE));
  }

  get hasLevel3(): boolean {
    return this.publishedLevelCount >= SKILL_LEARNING_LEVEL_COUNT.THREE;
  }

  get level1CompletedTextKey(): string {
    return this.hasLevel2
      ? SKILL_LEARNING_SESSION_MESSAGE.LEVEL_ONE_COMPLETED
      : SKILL_LEARNING_SESSION_MESSAGE.LEVEL_ONE_ONLY_COMPLETED;
  }

  get visibleLearningHelpRows(): LearningHelpRow[] {
    if (!this.hasLevel2) {
      return this.learningHelpRows.slice(
        SKILL_LEARNING_FIRST_INDEX,
        SKILL_LEARNING_LEVEL_COUNT.ONE,
      );
    }
    return this.hasLevel3
      ? this.learningHelpRows
      : this.learningHelpRows.slice(
        SKILL_LEARNING_FIRST_INDEX,
        SKILL_LEARNING_LEVEL_COUNT.THREE,
      );
  }

  trackByLearningHelpRow(index: number, row: LearningHelpRow): string {
    return row.titleKey;
  }

  trackByTopic(index: number, topic: string): string {
    return topic;
  }

  get isLevel1Passed(): boolean {
    return this.facade.isLevel1Passed(this.progress);
  }

  get isLevel1SelfTestPassed(): boolean {
    return this.facade.isLevel1SelfTestPassed(this.progress);
  }

  get isLevel2Passed(): boolean {
    return this.facade.isLevel2Passed(this.progress);
  }

  get isLevel2TestReviewPassed(): boolean {
    return !!this.progress && (
      !!this.progress.level2TestReviewPassedAt
      || this.progress.level2TestReviewTaskStatus === SKILL_PRACTICE_TASK_STATUS.COMPLETED
    );
  }

  get isLevel2Locked(): boolean {
    return !this.isLevelAvailable(SKILL_LEARNING_LEVEL_TAB.LEVEL_2);
  }

  get isLevel3Locked(): boolean {
    return !this.isLevelAvailable(SKILL_LEARNING_LEVEL_TAB.LEVEL_3);
  }

  get isLevel3InDevelopment(): boolean {
    return !this.hasPracticeLevelData(this.specialization && this.specialization.practiceLevel3);
  }

  get canCreateLevel1Task(): boolean {
    return false;
  }

  get shouldShowLevel1Task(): boolean {
    return false;
  }

  get shouldShowLevel1NextStepGuidance(): boolean {
    return !this.isLevel1Passed && !this.isLevel1SelfTestPassed;
  }

  get shouldEmphasizeLevel1TaskPlacement(): boolean {
    return false;
  }

  get hasActiveLevel1Task(): boolean {
    return false;
  }

  get isLevel1TaskStartDisabled(): boolean {
    return true;
  }

  get isLevel2TaskStartDisabled(): boolean {
    return this.isLevel2Locked;
  }

  get isLevel2TestReviewDisabled(): boolean {
    return this.isLevel2Locked || !this.progress || !this.progress.canCreateLevel2TestReviewTask;
  }

  get isLevel2PracticeDisabled(): boolean {
    return this.isLevel2Locked || !this.progress || !this.progress.canCreateLevel2PracticeTask;
  }

  get isLevel3TaskStartDisabled(): boolean {
    return this.isLevel3Locked || this.isLevel3InDevelopment;
  }

  get shouldLoadLevel3TaskOnInit(): boolean {
    return !!this.progress && !this.isLevel3TaskStartDisabled;
  }

  get specializationDescription(): string {
    return this.specialization
      ? this.localizedText(this.specialization.description, this.specialization.descriptionTranslation)
      : '';
  }

  get theoryDescription(): string {
    return this.specialization && this.specialization.theoryLevel
      ? this.localizedText(
        this.specialization.theoryLevel.description,
        this.specialization.theoryLevel.descriptionTranslation,
      )
      : '';
  }

  get level2ReviewDirections(): string {
    const topics = this.specialization && this.specialization.theoryLevel
      ? this.specialization.theoryLevel.topics || []
      : [];
    const topicTranslations = this.specialization && this.specialization.theoryLevel
      ? this.specialization.theoryLevel.topicTranslations || []
      : [];
    const normalizedTopics = topics
      .map((topic, index) => this.localizedText(topic, topicTranslations[index]))
      .filter(topic => topic.length > 0);
    if (normalizedTopics.length > 0) {
      return normalizedTopics.join('\n');
    }
    return this.specialization && this.specialization.theoryLevel
      ? this.localizedText(
        this.specialization.theoryLevel.description,
        this.specialization.theoryLevel.descriptionTranslation,
      )
      : '';
  }

  get level2PracticeDescription(): string {
    return this.specialization && this.specialization.practiceLevel2
      ? this.localizedText(
        this.specialization.practiceLevel2.description,
        this.specialization.practiceLevel2.descriptionTranslation,
      )
      : '';
  }

  get level3PracticeDescription(): string {
    return this.specialization && this.specialization.practiceLevel3
      ? this.localizedText(
        this.specialization.practiceLevel3.description,
        this.specialization.practiceLevel3.descriptionTranslation,
      )
      : '';
  }

  get sessionDescription(): string {
    return '';
  }

  get level1TaskLockedMessageKey(): string {
    if (this.progress
      && this.progress.level1TaskStatus === SKILL_PRACTICE_TASK_STATUS.CANCELLED
      && !this.canCreateLevel1Task) {
      return 'SKILL_LEARNING_SESSION_LEVEL1_TASK_COOLDOWN';
    }
    return 'SKILL_LEARNING_SESSION_LEVEL1_TASK_LOCKED';
  }

  onSelectLevel(viewMode: SkillLearningLevelTab): void {
    if (!this.isLevelAvailable(viewMode)) {
      return;
    }
    this.keepTestResultAfterProgressReload = false;
    this.viewMode = viewMode;
    this.cdr.markForCheck();
  }

  onTestCompleted(result: SkillTestResultResponse): void {
    if (result && result.passed) {
      this.keepTestResultAfterProgressReload = true;
      this.refreshProgressAfterCompletion();
    }
    this.cdr.markForCheck();
  }

  onNextLevelRequested(): void {
    if (!this.canRequestNextLevel) {
      return;
    }
    const nextLevel = this.activeLevelNumber + SKILL_LEARNING_LEVEL_COUNT.ONE;
    if (nextLevel <= this.publishedLevelCount) {
      this.onSelectLevel(<SkillLearningLevelTab>(nextLevel - SKILL_LEARNING_LEVEL_COUNT.ONE));
    }
    this.levelActionErrorKey = SKILL_LEARNING_EMPTY_TEXT;
  }

  onStartLevel2(): void {
    this.onSelectLevel(SKILL_LEARNING_LEVEL_TAB.LEVEL_2);
  }

  onStartLevel3(): void {
    this.onSelectLevel(SKILL_LEARNING_LEVEL_TAB.LEVEL_3);
  }

  onRetryProgress(): void {
    this.loadProgress();
  }

  onPracticeLevelCompleted(): void {
    this.refreshProgressAfterCompletion();
    this.cdr.markForCheck();
  }

  private refreshProgressAfterCompletion(): void {
    if (this.isProgressLoading) {
      this.completionRefreshQueued = true;
      return;
    }
    this.loadProgress();
  }

  onBackToList(): void {
    this.router.navigate([SKILL_LEARNING_ROUTE.LEARNING]);
  }

  private subscribeToLevelActionErrors(): void {
    this.facade.onLevelActionError$
      .takeUntil(this.destroy$)
      .subscribe(error => this.handleLevelActionError(error));
  }

  private handleLevelActionError(error: SkillLearningErrorResponse): void {
    if (this.destroyed || !this.specialization) { return; }
    this.routingNotConfigured = !!error && error.code === ROUTING_NOT_CONFIGURED;
    this.levelActionErrorKey = this.routingNotConfigured ? ROUTING_REQUIRED_KEY
      : error && error.code === SKILL_CONFIGURATION_ERROR.LEVEL_NOT_AVAILABLE
      ? SKILL_LEARNER_ACTION_MESSAGE.LEVEL_NOT_AVAILABLE
      : SKILL_LEARNER_ACTION_MESSAGE.REJECTED;
    this.cdr.markForCheck();
    this.reloadAfterLevelActionError();
  }

  get routingEditorPath(): string {
    return this.routingNotConfigured && this.specialization && this.specialization.canManageParticipantGroups
      ? SKILL_LEARNING_ROUTE.SPECIALIZATIONS + ROUTE_SEPARATOR + encodeURIComponent(this.specializationId) + EDIT_ROUTE
      : SKILL_LEARNING_EMPTY_TEXT;
  }

  private reloadAfterLevelActionError(): void {
    this.facade.fetchSpecializationDetails(this.specializationId)
      .takeUntil(this.contextChanged$)
      .takeUntil(this.destroy$)
      .subscribe(
        specialization => {
          this.specialization = specialization;
          this.loadProgress();
        },
        () => this.cdr.markForCheck(),
      );
  }

  private loadData(): void {
    this.isLoading = true;
    this.hasError = false;
    this.translationRefreshAttempts = INITIAL_REFRESH_ATTEMPTS;
    this.clearTranslationRefreshTimer();
    this.cdr.markForCheck();
    this.loadGeneralSettings();

    this.facade.fetchSpecializationDetails(this.specializationId)
      .takeUntil(this.contextChanged$)
      .takeUntil(this.destroy$)
      .subscribe(spec => this.acceptSpecialization(spec), () => this.rejectSpecialization());
  }

  private acceptSpecialization(specialization: SkillSpecializationResponse): void {
    this.specialization = specialization;
    this.scheduleTranslationRefresh();
    this.loadProgress();
    this.cdr.markForCheck();
  }

  private rejectSpecialization(): void {
    this.hasError = true;
    this.isLoading = false;
    this.cdr.markForCheck();
  }

  private loadProgress(): void {
    if (this.destroyed || this.isProgressLoading) {
      return;
    }
    this.isProgressLoading = true;
    this.hasProgressError = false;
    this.cdr.markForCheck();
    this.facade.fetchUserProgress(this.specializationId)
      .take(SKILL_LEARNING_LEVEL_COUNT.ONE)
      .takeUntil(this.contextChanged$)
      .takeUntil(this.destroy$)
      .subscribe(
        progress => this.handleProgressLoaded(progress),
        () => this.handleProgressLoadError(),
      );
  }

  private handleProgressLoaded(progress: SkillProgressResponse): void {
    this.progress = progress;
    this.isProgressLoading = false;
    this.isLoading = false;
    if (this.loadQueuedCompletionProgress()) { return; }
    this.applyViewModeAfterProgressLoad();
    this.cdr.markForCheck();
  }

  private handleProgressLoadError(): void {
    this.hasProgressError = true;
    this.isProgressLoading = false;
    this.isLoading = false;
    if (this.loadQueuedCompletionProgress()) { return; }
    this.cdr.markForCheck();
  }

  private loadQueuedCompletionProgress(): boolean {
    if (!this.completionRefreshQueued) { return false; }
    this.completionRefreshQueued = false;
    this.loadProgress();
    return true;
  }

  private loadGeneralSettings(): void {
    this.facade.fetchGeneralSettings()
      .takeUntil(this.contextChanged$)
      .takeUntil(this.destroy$)
      .subscribe(
        settings => this.applyGeneralSettings(settings),
        () => this.applyGeneralSettings(null),
      );
  }

  private applyGeneralSettings(settings: SkillLearningGeneralSettingsResponse | null): void {
    this.generalSettings = mergeSkillLearningGeneralSettings(settings);
    this.cdr.markForCheck();
  }

  private determineViewMode(): void {
    if (!this.isLevelAvailable(this.viewMode)) {
      this.viewMode = this.resolveInitialViewMode();
      return;
    }
    if (this.viewMode === SKILL_LEARNING_LEVEL_TAB.LEVEL_2
      && this.isLevelAvailable(SKILL_LEARNING_LEVEL_TAB.LEVEL_3)) {
      this.viewMode = SKILL_LEARNING_LEVEL_TAB.LEVEL_3;
      return;
    }
    if (this.viewMode === SKILL_LEARNING_LEVEL_TAB.LEVEL_2
      || this.viewMode === SKILL_LEARNING_LEVEL_TAB.LEVEL_3) {
      return;
    }
    this.viewMode = this.resolveInitialViewMode();
  }

  private resolveInitialViewMode(): SkillLearningLevelTab {
    if (this.isLevelAvailable(SKILL_LEARNING_LEVEL_TAB.LEVEL_3)) {
      return SKILL_LEARNING_LEVEL_TAB.LEVEL_3;
    }
    return this.isLevelAvailable(SKILL_LEARNING_LEVEL_TAB.LEVEL_2)
      ? SKILL_LEARNING_LEVEL_TAB.LEVEL_2
      : SKILL_LEARNING_LEVEL_TAB.LEVEL_1;
  }

  private applyViewModeAfterProgressLoad(): void {
    if (this.keepTestResultAfterProgressReload && this.isLevelAvailable(this.viewMode)) {
      return;
    }
    this.keepTestResultAfterProgressReload = false;
    this.determineViewMode();
  }

  private hasPracticeLevelData(level: PracticeLevelResponse | null): boolean {
    if (!level) {
      return false;
    }
    return this.hasText(level.description) && (!!level.document || !!level.documentFileId);
  }

  private hasText(value: string): boolean {
    return !!value && value.trim().length > 0;
  }

  private scheduleTranslationRefresh(): void {
    this.clearTranslationRefreshTimer();
    if (!this.hasMissingViewTranslations(this.specialization)
        || this.translationRefreshAttempts >= this.translationRefreshMaxAttempts) {
      return;
    }
    this.translationRefreshTimer = setTimeout(
      () => this.refreshSpecializationTranslations(),
      this.translationRefreshDelayMs,
    );
  }

  private refreshSpecializationTranslations(): void {
    this.translationRefreshTimer = null;
    this.translationRefreshAttempts++;
    this.facade.fetchSpecializationDetails(this.specializationId)
      .takeUntil(this.contextChanged$)
      .takeUntil(this.destroy$)
      .subscribe(
        spec => {
          this.specialization = spec;
          this.cdr.markForCheck();
          this.scheduleTranslationRefresh();
        },
        () => this.scheduleTranslationRefresh(),
      );
  }

  private hasMissingViewTranslations(specialization: SkillSpecializationResponse): boolean {
    if (!specialization) {
      return false;
    }
    if (this.isMissingTranslation(specialization.name, specialization.nameTranslation)
        || this.isMissingTranslation(specialization.description, specialization.descriptionTranslation)) {
      return true;
    }
    return this.isTheoryTranslationMissing(specialization.theoryLevel)
      || this.isTheoryTranslationMissing(specialization.theoryLevel2)
      || this.isTheoryTranslationMissing(specialization.theoryLevel3)
      || this.isPracticeDescriptionTranslationMissing(specialization.practiceLevel2)
      || this.isPracticeDescriptionTranslationMissing(specialization.practiceLevel3);
  }

  private isTheoryTranslationMissing(level: TheoryLevelResponse | null | undefined): boolean {
    if (!level || this.isMissingTranslation(level.description, level.descriptionTranslation)) {
      return !!level;
    }
    const topics = level.topics || [];
    const translations = level.topicTranslations || [];
    return topics.some((topic, index) => this.isMissingTranslation(topic, translations[index]));
  }

  private isPracticeDescriptionTranslationMissing(level: PracticeLevelResponse | null): boolean {
    return !!level && this.isMissingTranslation(level.description, level.descriptionTranslation);
  }

  private isMissingTranslation(source: string, translation: TranslationBundle): boolean {
    return this.hasText(source) && !this.isStandaloneUrl(source) && !translation;
  }

  private isStandaloneUrl(value: string): boolean {
    const normalized = (value || '').trim().toLowerCase();
    return (normalized.indexOf('http://') === 0 || normalized.indexOf('https://') === 0)
      && !/\s/.test(normalized);
  }

  private clearTranslationRefreshTimer(): void {
    if (this.translationRefreshTimer !== null) {
      clearTimeout(this.translationRefreshTimer);
      this.translationRefreshTimer = null;
    }
  }

  private normalizeText(value: string): string {
    return (value || '').trim();
  }

  private localizedText(value: string, translation: TranslationBundle): string {
    return this.normalizeText(this.uiLocaleService.resolveContent(value, translation));
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/components/user/learning-session/learning-session-container.component.ts